import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Empty, Spin, Divider, Tooltip } from "antd";
import { FaPlus } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { MdWarning } from "react-icons/md";

import AnimatedMainContainer from "../../../../components/AnimatedMainContainer";
import SelectWithInternalLabel from "../../../../newUXComponents/SelectWithInternalLabel";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { useToast } from "../../../../hooks/toast";
import {
  listEventAccessRanges,
  deleteEventAccessRange,
} from "../../../../service/totemPositionConfiguration.service";

import {
  Container,
  TitleContainer,
  Title,
  FlexContainer,
  NewButton,
  ContentContainer,
  Cards,
  CardHeader,
  LabelInfo,
  IconsDiv,
  DeleteIcon,
  ViewButton,
} from "../../styles";
import { InfoBadgesRow, InfoBadge, ActiveSessionsList } from "./styles";

const EDIT_ROUTE_BASE = "/admin/totem-position-configuration/edit";
const ALL_EVENTS = "all";
const CONFIRM_REQUIRED_CODE = -50028;

const PositionConfigurationsList = () => {
  const intl = useIntl();
  const history = useHistory();
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [items, setItems] = useState([]);
  const [eventFilter, setEventFilter] = useState(ALL_EVENTS);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activeSessionsModalOpen, setActiveSessionsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [activeSessionsAreas, setActiveSessionsAreas] = useState([]);

  const fetchConfigurations = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await listEventAccessRanges(token);
      setItems(data || []);
    } catch (error) {
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "totem_position.error_loading_configurations",
        }),
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, intl, addToast]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const eventOptions = useMemo(
    () =>
      Array.from(
        new Map(
          items.map((item) => [
            item.eventId,
            { label: item.eventName, value: item.eventId },
          ])
        ).values()
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [items]
  );

  const filteredItems = useMemo(
    () =>
      eventFilter === ALL_EVENTS
        ? items
        : items.filter((item) => item.eventId === eventFilter),
    [items, eventFilter]
  );

  const handleGoToNew = useCallback(() => {
    history.push(EDIT_ROUTE_BASE);
  }, [history]);

  const handleGoToEdit = useCallback(
    (eventId) => {
      history.push(`${EDIT_ROUTE_BASE}/${eventId}`);
    },
    [history]
  );

  const openDeleteConfirmation = useCallback((eventId) => {
    setSelectedEventId(eventId);
    setDeleteModalOpen(true);
  }, []);

  const showDeleteSuccessToast = useCallback(() => {
    addToast({
      type: "success",
      title: intl.formatMessage({ id: "success" }),
      description: intl.formatMessage({
        id: "totem_position.config_delete_success",
      }),
    });
  }, [addToast, intl]);

  const showDeleteErrorToast = useCallback(
    (error) => {
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "totem_position.config_delete_error",
        }),
      });
    },
    [addToast, intl]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedEventId) return;
    setIsDeleting(true);
    try {
      await deleteEventAccessRange(token, selectedEventId, false);
      setDeleteModalOpen(false);
      setSelectedEventId(null);
      showDeleteSuccessToast();
      await fetchConfigurations();
    } catch (error) {
      const data = error?.response?.data;
      if (data?.code === CONFIRM_REQUIRED_CODE || data?.requiresConfirmation) {
        setActiveSessionsAreas(data?.areasWithActiveSessions || []);
        setDeleteModalOpen(false);
        setActiveSessionsModalOpen(true);
      } else {
        showDeleteErrorToast(error);
        setDeleteModalOpen(false);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [
    selectedEventId,
    token,
    showDeleteSuccessToast,
    showDeleteErrorToast,
    fetchConfigurations,
  ]);

  const handleConfirmForceDelete = useCallback(async () => {
    if (!selectedEventId) return;
    setIsDeleting(true);
    try {
      await deleteEventAccessRange(token, selectedEventId, true);
      setActiveSessionsModalOpen(false);
      setActiveSessionsAreas([]);
      setSelectedEventId(null);
      showDeleteSuccessToast();
      await fetchConfigurations();
    } catch (error) {
      showDeleteErrorToast(error);
      setActiveSessionsModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }, [
    selectedEventId,
    token,
    showDeleteSuccessToast,
    showDeleteErrorToast,
    fetchConfigurations,
  ]);

  const cancelDeletion = useCallback(() => {
    setSelectedEventId(null);
    setActiveSessionsAreas([]);
  }, []);

  const activeSessionsDescription = useMemo(() => {
    if (!activeSessionsAreas.length) return null;
    return (
      <>
        {intl.formatMessage({
          id: "totem_position.confirm_force_delete_description",
        })}
        <ActiveSessionsList>
          {activeSessionsAreas.map((area) => (
            <li key={area}>{area}</li>
          ))}
        </ActiveSessionsList>
      </>
    );
  }, [activeSessionsAreas, intl]);

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({
        id: "totem_position.page_title",
      })}
    >
      <Spin spinning={isLoading}>
        <Container>
          <TitleContainer>
            <FlexContainer>
              <Title>
                {intl.formatMessage({
                  id: "totem_position.list.title",
                })}
              </Title>
              <SelectWithInternalLabel
                label={intl.formatMessage({ id: "event" }) + ":"}
                options={eventOptions}
                value={eventFilter}
                onChange={setEventFilter}
                searchable
              />
            </FlexContainer>
            <NewButton onClick={handleGoToNew}>
              <FaPlus />
              {intl.formatMessage({
                id: "totem_position.list.new_button",
              })}
            </NewButton>
          </TitleContainer>

          {filteredItems.length === 0 && !isLoading && <Empty />}

          <ContentContainer>
            {filteredItems.map((item) => {
              const exceedsLimit =
                item.totalPositionsLimit !== null &&
                item.totalPositionsLimit !== undefined &&
                item.totalPositionsConfigured > item.totalPositionsLimit;

              return (
                <Cards key={item.eventId}>
                  <CardHeader>
                    <div>
                      <LabelInfo>
                        <strong>
                          {intl.formatMessage({ id: "event" })}:{" "}
                        </strong>
                        <Tooltip title={item.eventName} placement="topLeft">
                          {item.eventName}
                        </Tooltip>
                      </LabelInfo>
                    </div>

                    <IconsDiv>
                      <DeleteIcon
                        size={24}
                        onClick={() => openDeleteConfirmation(item.eventId)}
                      />
                    </IconsDiv>
                  </CardHeader>

                  <InfoBadgesRow>
                    <InfoBadge>
                      {intl.formatMessage(
                        {
                          id: "totem_position.list.badge.positions",
                        },
                        {
                          configured: item.totalPositionsConfigured ?? 0,
                          limit:
                            item.totalPositionsLimit !== null &&
                            item.totalPositionsLimit !== undefined
                              ? item.totalPositionsLimit
                              : intl.formatMessage({
                                  id: "totem_position.list.badge.no_limit",
                                }),
                        }
                      )}
                    </InfoBadge>
                    <InfoBadge
                      background="#ECFDF5"
                      borderColor="#A7F3D0"
                      color="#065F46"
                    >
                      {intl.formatMessage(
                        {
                          id: "totem_position.list.badge.areas",
                        },
                        { count: item.configuredAreas ?? 0 }
                      )}
                    </InfoBadge>
                    {exceedsLimit && (
                      <InfoBadge
                        background="#FEF2F2"
                        borderColor="#FECACA"
                        color="#B91C1C"
                      >
                        <MdWarning size={14} />
                        {intl.formatMessage({
                          id: "totem_position.list.badge.exceeds_limit",
                        })}
                      </InfoBadge>
                    )}
                  </InfoBadgesRow>

                  <Divider />

                  <ViewButton onClick={() => handleGoToEdit(item.eventId)}>
                    <FiExternalLink size={20} />
                    {intl.formatMessage({
                      id: "totem_position.list.view_configuration",
                    })}
                  </ViewButton>
                </Cards>
              );
            })}
          </ContentContainer>
        </Container>
      </Spin>

      <ConfirmationModal
        modalIsOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        confirmationFunction={handleConfirmDelete}
        cancelationFunction={cancelDeletion}
        title={intl.formatMessage({
          id: "totem_position.confirm_delete_title",
        })}
        description={intl.formatMessage({
          id: "totem_position.confirm_delete_description",
        })}
        confirmationText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
        isLoading={isDeleting}
      />

      <ConfirmationModal
        modalIsOpen={activeSessionsModalOpen}
        setIsOpen={setActiveSessionsModalOpen}
        confirmationFunction={handleConfirmForceDelete}
        cancelationFunction={cancelDeletion}
        title={intl.formatMessage({
          id: "totem_position.confirm_force_delete_title",
        })}
        description={activeSessionsDescription}
        confirmationText={intl.formatMessage({
          id: "totem_position.confirm_force_delete_button",
        })}
        cancelText={intl.formatMessage({ id: "cancel" })}
        isLoading={isDeleting}
      />
    </AnimatedMainContainer>
  );
};

export default PositionConfigurationsList;
